import { useTranslation } from 'react-i18next';

import type { Task } from '../../../api/types';
import { impact } from '../../../shared/lib/haptics';
import { useLongPress } from '../../../shared/lib/useLongPress';
import { Badge } from '../../../shared/ui/Badge';
import { Button } from '../../../shared/ui/Button';
import styles from './TaskCard.module.css';

interface TaskCardProps {
  task: Task;
  onOpen: (taskId: number) => void;
  // PARA-135 (AC-1, AC-4): короткий тап по телу карточки. Целевой статус
  // вычисляет экран (nextStatusForTap) — карточка про переходы не знает.
  onTakeIntoWork: (task: Task) => void;
  // PARA-135 (AC-6, AC-8): long press по телу и тап по иконке «редактировать».
  onEdit: (taskId: number) => void;
}

// PARA-122 (D3/D4, AC-3/AC-4) — строка бэклога по образцу
// etc/design-system/project/components/tasks/TaskCard.jsx:
//  - SMALL/MEDIUM: чекбокс-круг + title (+ бейдж "N/M" у MEDIUM с подзадачами,
//    иначе CTA "Разбить на подзадачи"); done → зачёркивание + --text-faint;
//  - BIG: квадрат-иконка (ромб) + title, ниже прогресс-бар + подпись прогресса +
//    ссылка "Открыть подзадачи →". Бейдж в ветке BIG не показывается (AC-4).
// Навигация без изменений: split-CTA/open используют существующий onOpen.
//
// PARA-135 (АР-1) — тело карточки (индикатор + заголовок) стало семантической
// <button>, а иконка «редактировать», Badge и CTA — её СОСЕДЯМИ, а не
// потомками: вложенные интерактивные элементы невалидны и ломают клавиатуру,
// а sibling-структура даёт «ровно одно действие» (AC-8a) структурно, без
// цепочек stopPropagation, и бесплатно даёт Enter/Space (AC-E4).
export function TaskCard({ task, onOpen, onTakeIntoWork, onEdit }: TaskCardProps): JSX.Element {
  const { t } = useTranslation('tasks');
  const isSmall = task.size === 'SMALL';
  const isBig = task.size === 'BIG';
  const hasSubtasks = task.subtasks_count > 0;
  const done = task.status === 'DONE';

  const bodyHandlers = useLongPress({
    onLongPress: () => {
      // AC-9: импульс срабатывает ДО открытия экрана правки.
      impact();
      onEdit(task.id);
    },
    onTap: () => onTakeIntoWork(task),
  });

  // АР-4: признак «в работе» — модификатор на корне + data-status. box-shadow
  // не входит в box model, поэтому высота карточки не меняется, а data-status
  // даёт стабильный селектор для RTL и Playwright (CSS-модули в jsdom не
  // вычисляются, ассертить на класс нельзя).
  const cardClassName =
    task.status === 'IN_PROGRESS' ? `${styles.card} ${styles.cardInProgress}` : styles.card;

  // AC-8: иконка обязана присутствовать в ОБЕИХ ветках (SMALL/MEDIUM/BIG)
  // независимо от наличия подзадач — у SMALL это единственный вход в правку
  // с клавиатуры и в desktop-Telegram. aria-label — существующий ключ.
  const editButton = (
    <button
      type="button"
      className={styles.editButton}
      aria-label={t('editTask')}
      data-testid={`task-edit-${task.id}`}
      onClick={() => onEdit(task.id)}
    >
      <svg className={styles.editIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M4.75 19.25l.75-3.5L15.6 5.65a1.9 1.9 0 0 1 2.75 2.75L8.25 18.5l-3.5.75Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path
          d="M14.25 7l2.75 2.75"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );

  if (isBig) {
    const pct = hasSubtasks
      ? Math.round((task.subtasks_done_count / task.subtasks_count) * 100)
      : 0;

    return (
      <div className={cardClassName} data-status={task.status} data-testid={`task-card-${task.id}`}>
        <div className={styles.row}>
          <button
            type="button"
            className={styles.body}
            data-testid={`task-body-${task.id}`}
            {...bodyHandlers}
          >
            <span className={styles.bigIndicator} data-testid="big-indicator">
              <span className={styles.diamond} />
            </span>
            <span className={styles.titleBig}>{task.title}</span>
          </button>
          {editButton}
        </div>
        {hasSubtasks ? (
          <div className={styles.bigDetails}>
            <div className={styles.progressTrack}>
              <div className={styles.progressFill} style={{ width: `${pct}%` }} />
            </div>
            <div className={styles.progressMeta}>
              <span className={styles.progressCaption}>
                {t('subtaskProgress', {
                  done: task.subtasks_done_count,
                  count: task.subtasks_count,
                })}
              </span>
              <button type="button" className={styles.openLink} onClick={() => onOpen(task.id)}>
                {t('openSubtasks')}
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.bigDetails}>
            <Button
              variant="secondary"
              className={styles.splitButton}
              onClick={() => onOpen(task.id)}
            >
              {t('splitCta')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cardClassName} data-status={task.status} data-testid={`task-card-${task.id}`}>
      <div className={styles.row}>
        <button
          type="button"
          className={styles.body}
          data-testid={`task-body-${task.id}`}
          {...bodyHandlers}
        >
          <span className={done ? styles.checkboxDone : styles.checkbox} aria-hidden="true">
            {done ? '✓' : ''}
          </span>
          <span className={done ? `${styles.title} ${styles.titleDone}` : styles.title}>
            {task.title}
          </span>
        </button>
        {!isSmall && hasSubtasks && (
          <Badge size={task.size.toLowerCase() as 'medium'} data-testid="subtasks-badge">
            {task.subtasks_done_count}/{task.subtasks_count}
          </Badge>
        )}
        {editButton}
      </div>
      {!isSmall && !hasSubtasks && (
        <div className={styles.ctaRow}>
          <Button
            variant="secondary"
            className={styles.splitButton}
            onClick={() => onOpen(task.id)}
          >
            {t('splitCta')}
          </Button>
        </div>
      )}
    </div>
  );
}
